import { PaginatedResponse, useApi } from '@/api';
import { Page } from '@/components';
import { currentSeason } from '@/utils';
import {
  Box,
  CurrencyValue,
  DataTable,
  EmptyBox,
  Flex,
  KpiCard,
  KpiSkeleton,
  RelativeDateTime,
  TableSkeleton,
  TextSearchFilter,
  useTableProps,
  WalletAddress
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useMemo } from 'react';

type ItemData = {
  wallet_address: string;
  datetime: string;
  position_points: string;
  tokens_per_second: string;
  referral_points: string;
  total_points: string;
  rank: null | number;
  invited_by: string;
};

type ExtendedPaginatedResponse = PaginatedResponse<ItemData[]> & {
  total_tokens: number;
  tokens_per_second: number;
  total_score: number;
  total_wallets: number;
  last_run: string;
};

export default function HomePage() {
  const { pagination, sorting, filters } = useTableProps({
    pageSize: 10,
    initialSorting: '-total_points',
    filtersDebounceTime: 500,
    initialFilters: {
      selectedOption: ''
    }
  });

  const [data, error] = useApi<ExtendedPaginatedResponse>({
    url: `/api/points/season${currentSeason()}/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      search: filters.filtersApplied['textSearch']
    }
  });

  const columns: TableColumn<ItemData>[] = useMemo(
    () => [
      {
        header: 'Rank',
        getCell: (row) => <Box color="muted">{row.rank}</Box>,
        sortingField: 'rank',
        columnSize: 'auto'
      },
      {
        header: 'Wallet',
        getCell: (row) => <WalletAddress address={row.wallet_address} />,
        sticky: true
      },
      {
        header: 'Invited By',
        getCell: (row) =>
          row.invited_by ? <WalletAddress address={row.invited_by} /> : '-',
        sticky: true
      },
      {
        header: 'Points',
        getCell: (row) => <CurrencyValue value={row.total_points} />,
        sortingField: 'total_points',
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      }
    ],
    []
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return (
      <Flex flexDirection="column" gap="s">
        <Flex gap="s" flexDirection={['column', 'row']}>
          <KpiSkeleton />
          <KpiSkeleton />
        </Flex>
        <TableSkeleton rows={15} columns={3} />
      </Flex>
    );
  }

  return (
    <Page>
      <Flex gap="s" flexDirection={['column', 'row']}>
        <KpiCard
          title="Total Points"
          data={{
            total_tokens: data['total_tokens']
          }}
          field="total_tokens"
          footer={
            <Flex gap="2xs" fontSize="-1">
              Last updated:
              {data['last_run'] ? (
                <RelativeDateTime value={data['last_run']} />
              ) : (
                '-'
              )}
            </Flex>
          }
        />
        <KpiCard
          title="N° of Wallets"
          data={{
            total_wallets: data['total_wallets']
          }}
          field="total_wallets"
          footer={<Flex height="-1" width="-1" />}
        />
      </Flex>
      {data['total_wallets'] > 0 && (
        <Flex flexDirection="column" gap="2xs">
          <Flex justifyContent="flex-end">
            <TextSearchFilter
              pagination={pagination}
              placeholder="Search by wallet"
              {...filters}
            />
          </Flex>
          <DataTable
            rows={data.results}
            columns={columns}
            getKey={(row) => row.wallet_address}
            sorting={sorting}
            pagination={{
              ...pagination,
              totalRecords: data.count
            }}
          />
        </Flex>
      )}
      {data['total_wallets'] === 0 && (
        <EmptyBox message="Campaign is not active yet" />
      )}
    </Page>
  );
}
